import { useRecordContext } from 'react-admin';
import { Stack } from '@mui/material';
import get from 'lodash/get';
import startCase from 'lodash/startCase';
import { COUNTRY_CODE_TO_FLAG } from '@/constants';
import { OneOffChecksTable, StatusHistoryTable, WorkerDocumentsTable } from '@/pages/Candidate/view/tables';
import { path } from '@/pages/Candidate/constants';
import { FCRAWorker } from '@/types/turn-api';
import {
  boolLabel,
  fieldIf,
  formatDate,
  formatPhone,
  getFlagsChips,
  getMatchChip,
  getOneOffCheckChips,
  pendingChecksLabel,
  text,
} from '@/pages/components/SummaryDetails/utils';
import type {
  CardSectionVM,
  FieldVM,
  HeaderVM,
  SummaryDetailsController,
  TableFieldVM,
  TableSectionVM,
} from '@/pages/components/SummaryDetails/types';
import {
  faAddressCard,
  faBoxes,
  faBuildings,
  faCircleUserClock,
  faClipboardCheck,
  faClipboardUser,
  faEnvelope,
  faFolders,
} from '@fortawesome/pro-regular-svg-icons';
import useResources from './useResources';

const useController = (): SummaryDetailsController => {
  const r = useRecordContext<FCRAWorker.Details>();
  const { logSSNCopy } = useResources();

  // sources
  const sActions = path('actions');
  const sCMRecheck = path('has_cm_recheck');
  const sDDPPAR = path('is_ddppar');
  const sDobEntered = path('candidate_provided_date_of_birth');
  const sDobStatus = path('dob_status');
  const sDocuments = path('documents');
  const sDriverLicenseNumber = path('drivers_license_number');
  const sDriverLicenseState = path('drivers_license_state');
  const sEmail = path('candidate_email');
  const sFirst = path('first_name');
  const sGender = path('gender');
  const sId = path('id');
  const sLast = path('last_name');
  const sManualBackgroundCheck = path('is_manual_background_check');
  const sMiddle = path('middle_name');
  const sNameEntered = path('candidate_provided_name');
  const sNameStatus = path('name_status');
  const sOneOffCheckReports = path('one_off_check_reports');
  const sPackageCountry = path('package.country_code');
  const sPackageDrugTestType = path('package.drug_test_type');
  const sPackageFlags = path('package.active_flags');
  const sPackageId = path('package.package_id');
  const sPackageName = path('package.name');
  const sPackageOneOffChecks = path('package.one_off_checks');
  const sPartnerId = path('partner.id');
  const sPartnerFlags = path('partner.active_flags');
  const sPartnerName = path('partner.name');
  const sParentPartnerName = path('partner.parent_partner.name');
  const sPhone = path('phone_number');
  const sProfileStatus = path('profile_status');
  const sPublicDob = path('public_provided_date_of_birth');
  const sPublicName = path('public_provided_name');
  const sPublicationDate = path('date_initiated');
  const sReferenceId = path('reference_id');
  const sSource = path('source');
  const sSSN = path('ssn');
  const sSSN4 = path('last_four_ssn');
  const sStatusHistory = path('status_history');
  const sTurnId = path('turn_id');
  const sZip = path('zipcode');

  const nameEntered = text(r, sNameEntered) || `${text(r, sFirst)} ${text(r, sLast)}`.trim();
  const ssn = text(r, sSSN);
  const ssn4 = text(r, sSSN4);
  const ssnCopyLabel = ssn ? 'SSN' : 'SSN Last Four Digits';
  const pending = pendingChecksLabel(r, sActions);

  const nameMatchChip = getMatchChip(text(r, sNameStatus) as FCRAWorker.MatchType, 'Name');
  const dobMatchChip = getMatchChip(text(r, sDobStatus) as FCRAWorker.MatchType, 'DOB');

  const countryCodeField = fieldIf(r, sPackageCountry, {
    key: sPackageCountry,
    label: 'Country Code',
    value: text(r, sPackageCountry),
  });

  const countryFlag = COUNTRY_CODE_TO_FLAG?.[(countryCodeField as FieldVM)?.value as string];

  const manualBgRaw = get(r, sManualBackgroundCheck);
  const manualBackgroundCheck =
    manualBgRaw === true ||
    manualBgRaw === 1 ||
    manualBgRaw === '1' ||
    manualBgRaw === 'True' ||
    manualBgRaw === 'true';

  const sourceText = text(r, sSource);

  const header: HeaderVM = {
    countryFlag,
    displayName: nameEntered,
    id: text(r, sId),
    manualBackgroundCheck,
    profileStatus: sProfileStatus,
    referenceId: text(r, sReferenceId),
    turnId: text(r, sTurnId),
    ...(sourceText ? { source: sourceText } : {}),
  };

  const identitySection: CardSectionVM = {
    title: 'Identity',
    icon: faAddressCard,
    actions:
      nameMatchChip || dobMatchChip ? (
        <Stack key="name-dob-match-chip" direction="row" spacing={1}>
          {nameMatchChip}
          {dobMatchChip}
        </Stack>
      ) : null,
    fields: [
      fieldIf(r, sFirst, { key: sFirst, label: 'First Name', value: text(r, sFirst) })!,
      fieldIf(r, sLast, { key: sLast, label: 'Last Name', value: text(r, sLast) })!,
      fieldIf(r, sMiddle, { key: sMiddle, label: 'Middle Name', value: text(r, sMiddle) })!,
      fieldIf(r, sNameEntered, { key: sNameEntered, label: 'Name Entered', value: text(r, sNameEntered) })!,
      fieldIf(r, sPublicName, { key: sPublicName, label: 'Public Records Name', value: text(r, sPublicName) })!,
      fieldIf(r, sDobEntered, {
        key: sDobEntered,
        label: 'DOB Entered',
        value: formatDate(text(r, sDobEntered)),
      })!,
      fieldIf(r, sPublicDob, {
        key: sPublicDob,
        label: 'Public Records DOB',
        value: formatDate(text(r, sPublicDob)),
      })!,
      fieldIf(r, sGender, { key: sGender, label: 'Gender', value: text(r, sGender) })!,
      fieldIf(r, sDriverLicenseNumber, {
        key: sDriverLicenseNumber,
        label: 'Driver License Number',
        value: text(r, sDriverLicenseNumber),
      })!,
      fieldIf(r, sDriverLicenseState, {
        key: sDriverLicenseState,
        label: 'Driver License State',
        value: text(r, sDriverLicenseState),
      })!,
      fieldIf(r, sZip, { key: sZip, label: 'Zipcode', value: text(r, sZip), copyText: text(r, sZip) })!,
      fieldIf(r, sSSN4, {
        copyLabel: ssnCopyLabel,
        copyText: ssn || ssn4,
        key: sSSN4,
        label: 'SSN Last Four Digits',
        onCopyToClipboard: () => logSSNCopy(),
        tooltipTitle: ssn
          ? 'Click to copy the full SSN (9 digits). Only the last four are shown here.'
          : 'Copy last four digits',
        value: ssn ? `${ssn4}*` : ssn4,
        valueSx: ssn
          ? (theme) => ({
              color: theme.palette.turn_violet.main,
              fontWeight: 600,
              maxWidth: 'none',
            })
          : undefined,
      })!,
    ].filter(Boolean) as FieldVM[],
  };

  const contactSection: CardSectionVM = {
    title: 'Contact',
    icon: faEnvelope,
    fields: [
      fieldIf(r, sEmail, { key: sEmail, label: 'Email', value: text(r, sEmail), copyText: text(r, sEmail) })!,
      fieldIf(r, sPhone, {
        key: sPhone,
        label: 'Phone Number',
        value: formatPhone(text(r, sPhone)),
        copyText: text(r, sPhone),
      })!,
    ].filter(Boolean) as FieldVM[],
  };

  const partnerSection: CardSectionVM = {
    title: 'Partner',
    icon: faBuildings,
    fields: [
      fieldIf(r, sPartnerId, {
        key: sPartnerId,
        label: 'ID',
        value: text(r, sPartnerId),
        copyText: text(r, sPartnerId),
      })!,
      fieldIf(r, sPartnerName, { key: sPartnerName, label: 'Name', value: text(r, sPartnerName) })!,
      fieldIf(r, sParentPartnerName, {
        key: sParentPartnerName,
        label: 'Parent Partner Name',
        value: text(r, sParentPartnerName),
      })!,
    ].filter(Boolean) as FieldVM[],
    moreDetails: {
      customFields: [getFlagsChips(r, sPartnerFlags)],
      fields: [],
    },
  };

  const packageSection: CardSectionVM = {
    title: 'Package',
    icon: faBoxes,
    fields: [
      fieldIf(r, sPackageId, {
        key: sPackageId,
        label: 'ID',
        value: text(r, sPackageId),
        copyText: text(r, sPackageId),
      })!,
      fieldIf(r, sPackageName, { key: sPackageName, label: 'Name', value: text(r, sPackageName) })!,
      countryCodeField!,
    ].filter(Boolean) as FieldVM[],
    moreDetails: {
      customFields: [getFlagsChips(r, sPackageFlags), getOneOffCheckChips(r, sPackageOneOffChecks)],
      fields: [
        fieldIf(r, sPackageDrugTestType, {
          key: sPackageDrugTestType,
          label: 'Drug Test Type',
          value: text(r, sPackageDrugTestType),
        })!,
      ].filter(Boolean) as FieldVM[],
    },
  };

  const checksSection: CardSectionVM = {
    title: 'BGC Info',
    icon: faClipboardUser,
    fields: [
      fieldIf(r, sPublicationDate, {
        key: sPublicationDate,
        label: 'Publication Date',
        value: formatDate(text(r, sPublicationDate)),
      })!,
      fieldIf(r, sCMRecheck, { key: sCMRecheck, label: 'CM Recheck', value: boolLabel(get(r, sCMRecheck)) })!,
      fieldIf(r, sDDPPAR, {
        key: sDDPPAR,
        label: 'DDPPAR',
        value:
          get(r, sDDPPAR) === 'True'
            ? 'This applicant requested to have their Personal Identifiable Information deleted'
            : 'No',
      })!,
      fieldIf(r, sActions, { key: sActions, label: 'Pending Checks', value: pending || '—' })!,
    ].filter(Boolean) as FieldVM[],
  };

  const tablesSection: TableSectionVM = {
    title: 'Tables',
    fields: [
      fieldIf(r, sOneOffCheckReports, {
        component: <OneOffChecksTable source={sOneOffCheckReports} />,
        icon: faClipboardCheck,
        label: 'One Off Checks',
        sx: { card: { width: '100%', maxWidth: '100%' } },
      })!,
      fieldIf(r, sStatusHistory, {
        component: <StatusHistoryTable source={sStatusHistory} />,
        icon: faCircleUserClock,
        label: 'Status History',
        sx: { card: { width: '100%', maxWidth: '100%' } },
      })!,
      fieldIf(r, sDocuments, {
        component: <WorkerDocumentsTable source={sDocuments} />,
        icon: faFolders,
        label: 'Partner Worker Documents',
        sx: { card: { width: '100%', maxWidth: '100%' } },
      })!,
    ].filter(Boolean) as TableFieldVM[],
  };

  return {
    header,
    leftColumn: [identitySection, checksSection],
    rightColumn: [contactSection, partnerSection, packageSection],
    tablesSection,
  };
};

export default useController;
